import React, { useMemo, useState } from "react";
import { Box, Flex, Text, Input } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import NewsSlider from "../components/NewsSlider";
import PromotionBar from "../components/PromotionBar";
import {
  getMyAttendanceRateByMonthApi,
  getMyEvaluationSummaryByMonthApi,
} from "../api/dashboard.api";
import { monthKey } from "../utils/format";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../config";
import { appImages } from "../assets/appicons";
import { AppIcon, IconTile, TileGrid, EvalMetric, APP } from "../components/mobileUi";
import {
  DESK,
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  useIsDesktop,
} from "../components/desktopUi";

const normalize = (v = "") =>
  v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").trim();

const SUPPORT_DEPARTMENT_CODES = [
  "PHONG_HANH_CHINH_NHAN_SU",
  "PHONG_KE_TOAN",
  "HO_TRO_BAN_HANG_CRM",
];

// Nhãn + màu xếp loại đánh giá (khớp dashboard.constant của app)
const EVAL_LABELS = {
  XUAT_SAC: "Xuất sắc",
  DAT: "Đạt",
  CANH_BAO: "Cảnh báo",
  CANH_BAO_DO: "Cảnh báo đỏ",
  CAN_HO_TRO: "Cần hỗ trợ",
  CHUA_XEP_LOAI: "Chưa xếp loại",
};
const EVAL_COLORS = {
  XUAT_SAC: "#1277EB",
  DAT: "#2ABA82",
  CANH_BAO_DO: "#E5484D",
  CAN_HO_TRO: "#8B5CF6",
};
const evalLabel = (code) => EVAL_LABELS[code] || "Chưa xếp loại";
const evalColor = (code) => EVAL_COLORS[code] || "#F79D0D";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useEmployeeAuth();

  // Logic hiển thị chức năng quản lý (khớp app): quản lý hoặc phòng ban hỗ trợ đặc biệt
  const isManager = (user?.jobPosition?.code || "") !== "NHAN_VIEN";
  const isSupportDepartment = SUPPORT_DEPARTMENT_CODES.includes(
    user?.supportDepartment?.code || ""
  );
  const showManagement = isManager || isSupportDepartment;

  const rateQuery = useQuery({
    queryKey: ["emp", "dash-rate", monthKey()],
    queryFn: () => getMyAttendanceRateByMonthApi(monthKey()),
  });
  const evalQuery = useQuery({
    queryKey: ["emp", "dash-eval", monthKey()],
    queryFn: () => getMyEvaluationSummaryByMonthApi(monthKey()),
    retry: false,
  });

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const isDesktop = useIsDesktop();

  const displayName = user?.fullName || "Admin";
  const month = new Date().getMonth() + 1;

  const avatarUri = useMemo(() => {
    const raw = user?.avatar;
    if (!raw) return appImages.empty_avatar;
    if (raw.startsWith("http")) return raw;
    const base = EMP_API_BASE_URL.replace(/\/+$/, "");
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return `${base}${path}`;
  }, [user?.avatar]);

  const totalWork = rateQuery.data?.totalWorkingDayRate ?? 0;
  const expectedWork = rateQuery.data?.expectedWorkRate;
  const evalData = evalQuery.data || {};

  const go = (to) => () => navigate(to);
  // Chức năng chưa clone -> đưa sang trang bảo trì (khớp app)
  const noop = () => navigate(`${EMP_BASE_PATH}/bao-tri`);

  // Danh sách chức năng — nhóm theo mục (khớp app). Mục chưa làm -> onPress rỗng.
  const ACTIONS = useMemo(
    () => [
      { id: "check-in", label: "Chấm công", section: "Chuyên cần", icon: appImages.icon_cham_cong, onPress: go(`${EMP_BASE_PATH}/chuyen-can`) },
      { id: "outside", label: "Đăng ký\nchấm công\nngoài", section: "Chuyên cần", icon: appImages.icon_dky_cn, onPress: go(`${EMP_BASE_PATH}/cham-cong-ngoai`) },
      { id: "makeup", label: "Đăng ký\nlàm bù", section: "Chuyên cần", icon: appImages.icon_dk_lam_bu, onPress: go(`${EMP_BASE_PATH}/lam-bu`) },
      { id: "leave", label: "Xin nghỉ\nphép", section: "Chuyên cần", icon: appImages.icon_xin_nghi_phep, onPress: go(`${EMP_BASE_PATH}/nghi-phep`) },

      { id: "project", label: "Dự án", section: "Tài chính", icon: appImages.icon_Du_an, onPress: go(`${EMP_BASE_PATH}/tai-chinh/du-an`) },
      { id: "open-sale", label: "Đợt mở\nbán", section: "Tài chính", icon: appImages.icon_dot_mo_ban, onPress: go(`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban`) },
      { id: "customer", label: "Khách\nhàng", section: "Tài chính", icon: appImages.icon_khach_hang, onPress: go(`${EMP_BASE_PATH}/tai-chinh/khach-hang`) },
      { id: "booking", label: "Booking", section: "Tài chính", icon: appImages.icon_booking, onPress: go(`${EMP_BASE_PATH}/tai-chinh/booking`) },
      { id: "locking", label: "Locking", section: "Tài chính", icon: appImages.icon_looking, onPress: go(`${EMP_BASE_PATH}/tai-chinh/locking`) },
      { id: "ttkq", label: "TTKQ", section: "Tài chính", icon: appImages.icon_TTKQ, onPress: go(`${EMP_BASE_PATH}/tai-chinh/ttkq`) },
      { id: "hdmb", label: "Hợp đồng\nmua bán", section: "Tài chính", icon: appImages.icon_HDMB, onPress: go(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban`) },
      { id: "transfer-gd", label: "Đăng ký\nchuyển\ngiao dịch", section: "Tài chính", icon: appImages.icon_dang_ky_chuen_GD, onPress: go(`${EMP_BASE_PATH}/tai-chinh/chuyen-giao-dich`) },
      { id: "ctv", label: "Cộng\ntác viên", section: "Tài chính", icon: appImages.icon_ctv, onPress: go(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien`) },
      { id: "income", label: "Thu nhập\ncá nhân", section: "Tài chính", icon: appImages.icon_Thu_nhap_ca_nhan, onPress: go(`${EMP_BASE_PATH}/tai-chinh/thu-nhap`) },
      { id: "ccmg", label: "Chứng chỉ\nmôi giới", section: "Tài chính", icon: appImages.icon_TTKQ, onPress: go(`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`) },

      { id: "job", label: "Giới thiệu việc làm", section: "Tiện ích", icon: appImages.icon_gioi_thieu_viec_lam, onPress: go(`${EMP_BASE_PATH}/gioi-thieu-viec-lam`) },

      ...(showManagement
        ? [
            { id: "mgmt-staff", label: "Quản lý\nnhân viên", section: "Quản lý", icon: appImages.icon_QLNV, onPress: go(`${EMP_BASE_PATH}/quan-ly/nhan-vien`) },
            { id: "mgmt-attendance", label: "Quản lý\nchuyên cần", section: "Quản lý", icon: appImages.icon_QLCC, onPress: go(`${EMP_BASE_PATH}/quan-ly/chuyen-can`) },
            { id: "mgmt-sell", label: "Quản lý\nbán hàng", section: "Quản lý", icon: appImages.icon_QLBH, onPress: go(`${EMP_BASE_PATH}/quan-ly/ban-hang`) },
            { id: "mgmt-report", label: "Báo cáo\nthống kê", section: "Quản lý", icon: appImages.icon_BC_Thong_ke, onPress: go(`${EMP_BASE_PATH}/quan-ly/bao-cao`) },
            { id: "mgmt-leave", label: "Phê duyệt\nnghỉ phép", section: "Quản lý", icon: appImages.icon_phe_duyet_np, onPress: go(`${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep`) },
            { id: "mgmt-outside", label: "Phê duyệt\nchấm công\nngoài", section: "Quản lý", icon: appImages.icon_phe_duyet_cong_ngoai, onPress: go(`${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai`) },
            { id: "mgmt-ccmg", label: "Quản lý\nCCMG", section: "Quản lý", icon: appImages.icon_phieu_dang_ky, onPress: go(`${EMP_BASE_PATH}/quan-ly/ccmg`) },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showManagement]
  );

  const SECTIONS = ["Chuyên cần", "Tài chính", "Tiện ích"];

  const searchResults = useMemo(() => {
    const kw = normalize(searchValue);
    const base = kw
      ? ACTIONS.filter((a) => `${normalize(a.label)} ${normalize(a.section)}`.includes(kw))
      : ACTIONS;
    return base.slice(0, kw ? 10 : 6);
  }, [ACTIONS, searchValue]);

  const NEWS = useMemo(
    () => [
      {
        id: "n1",
        image: appImages.bg_slider_tai_lieu,
        title: "Đào tạo",
        onPress: () => navigate(`${EMP_BASE_PATH}/dao-tao`),
      },
      { id: "n2", image: appImages.bg_slider_vinh_danh, title: "Vinh danh", onPress: noop },
      { id: "n3", image: appImages.bg_slider_tin_tuc, title: "Tin tức", onPress: noop },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const HeaderIcon = ({ src, onClick }) => (
    <Flex as="button" type="button" onClick={onClick} align="center" justify="center" w="28px" h="28px">
      <AppIcon src={src} size={24} color="#FFFFFF" />
    </Flex>
  );

  // ===================== Bản WEB (desktop) =====================
  // Cùng dữ liệu, cùng điều hướng; chỉ đổi cách trình bày sang style admin.
  if (isDesktop) {
    const DeskTile = ({ item }) => (
      <Flex
        as="button"
        type="button"
        onClick={item.onPress}
        align="center"
        gap="12px"
        p="12px"
        borderRadius="10px"
        border="1px solid"
        borderColor={DESK.border}
        bg={DESK.surface}
        _hover={{ borderColor: DESK.primary, boxShadow: "0 2px 10px rgba(8,20,45,0.08)" }}
        transition="all .15s"
        textAlign="left"
      >
        <Flex
          w="44px"
          h="44px"
          flexShrink={0}
          borderRadius="10px"
          bg="var(--fill-avatar)"
          align="center"
          justify="center"
        >
          <AppIcon src={item.icon} size={22} color={APP.tabActive} />
        </Flex>
        <Text fontSize="14px" fontWeight={600} color={DESK.info} whiteSpace="normal">
          {item.label.replace(/\n/g, " ")}
        </Text>
      </Flex>
    );

    const DeskSection = ({ title, items }) =>
      items.length ? (
        <DesktopPanel>
          <Text fontSize="16px" fontWeight={600} color={DESK.title}>
            {title}
          </Text>
          <Box
            mt="16px"
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))"
            gap="16px"
          >
            {items.map((a) => (
              <DeskTile key={a.id} item={a} />
            ))}
          </Box>
        </DesktopPanel>
      ) : null;

    return (
      <DesktopPage
        title={`Hi, ${displayName}`}
        subtitle={`Đánh giá cá nhân trong tháng ${month}`}
        right={
          <DesktopSearch
            placeholder="Tìm chức năng..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            maxW="360px"
          />
        }
      >
        {rateQuery.data?.promotionBar?.message ? (
          <PromotionBar
            key={rateQuery.dataUpdatedAt}
            message={rateQuery.data.promotionBar.message}
            topInset={88}
            onClick={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)}
          />
        ) : null}

        <DesktopStatRow
          items={[
            {
              key: "rank",
              title: "Xếp loại",
              total: evalLabel(evalData.evaluationStatus),
              color: evalColor(evalData.evaluationStatus),
            },
            { key: "consult", title: "Số cuộc tư vấn", total: evalData.consultCount ?? 0 },
            { key: "lead", title: "Số lead", total: evalData.leadCount ?? 0 },
            { key: "trans", title: "Số giao dịch", total: evalData.transactionCount ?? 0 },
            {
              key: "work",
              title: "Số công",
              total: expectedWork != null ? `${totalWork}/${expectedWork}` : `${totalWork}`,
            },
          ]}
        />

        {searchValue ? (
          <DesktopPanel>
            <Text fontSize="16px" fontWeight={600} color={DESK.title}>
              Kết quả tìm kiếm
            </Text>
            {searchResults.length ? (
              <Box
                mt="16px"
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(220px, 1fr))"
                gap="16px"
              >
                {searchResults.map((a) => (
                  <DeskTile key={a.id} item={a} />
                ))}
              </Box>
            ) : (
              <Text mt="16px" fontSize="14px" color={DESK.label}>
                Không tìm thấy chức năng phù hợp
              </Text>
            )}
          </DesktopPanel>
        ) : null}

        {SECTIONS.map((section) => (
          <DeskSection
            key={section}
            title={section}
            items={ACTIONS.filter((a) => a.section === section)}
          />
        ))}

        {showManagement && (
          <DeskSection
            title="Quản lý"
            items={ACTIONS.filter((a) => a.section === "Quản lý")}
          />
        )}

        <DesktopPanel>
          <Text fontSize="16px" fontWeight={600} color={DESK.title} mb="12px">
            Tin tức nội bộ
          </Text>
          <NewsSlider items={NEWS} />
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg={APP.surface} minH="100%">
      {/* ===== Nhắc nhở chấm công (khớp PromotionBar của app) =====
          Hiện lại mỗi lần quay về Dashboard: `key` đổi theo mỗi lần
          getMyAttendanceRateByMonth trả về thành công (giống promoNonce của app) */}
      {rateQuery.data?.promotionBar?.message ? (
        <PromotionBar
          key={rateQuery.dataUpdatedAt}
          message={rateQuery.data.promotionBar.message}
          onClick={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)}
        />
      ) : null}

      {/* ===== Header xanh (background_info) ===== */}
      <Box
        position="relative"
        backgroundImage={`url(${appImages.background_info})`}
        backgroundSize="cover"
        backgroundPosition="center"
        bgColor={APP.primary100}
        pt="20px"
        px="20px"
        pb="32px"
      >
        <Flex h="54px" align="center" justify="space-between">
          <Flex align="center" gap="10px">
            <Box
              w="40px"
              h="40px"
              borderRadius="full"
              bg="#DCE7F7"
              backgroundImage={`url(${avatarUri})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
            <Text fontSize="16px" fontWeight={600} color="white">
              Hi, {displayName}
            </Text>
          </Flex>
          <Flex align="center" gap="14px">
            <HeaderIcon src={appImages.icon_search} onClick={() => setSearchOpen((p) => !p)} />
            <HeaderIcon src={appImages.icon_notification} onClick={noop} />
          </Flex>
        </Flex>

        {/* Thẻ đánh giá cá nhân trong tháng */}
        <Box
          mt="14px"
          mx="-6px"
          bg={APP.surface}
          borderRadius="16px"
          px="12px"
          pt="10px"
          pb="14px"
          cursor="pointer"
          onClick={() => navigate(`${EMP_BASE_PATH}/danh-gia-ca-nhan`)}
        >
          <Text fontSize="15px" fontWeight={600} color={APP.black}>
            Đánh giá cá nhân trong tháng {month}
          </Text>
          <Flex mt="8px" align="stretch">
            <EvalMetric
              label="Xếp loại"
              value={evalLabel(evalData.evaluationStatus)}
              valueColor={evalColor(evalData.evaluationStatus)}
              isStatus
            />
            <EvalMetric label="Số cuộc tư vấn" value={evalData.consultCount ?? 0} icon={appImages.icon_phone_call} iconColor="#2F80ED" />
            <EvalMetric label="Số lead" value={evalData.leadCount ?? 0} icon={appImages.icon_user_plus} iconColor="#2ABA82" />
            <EvalMetric label="Số giao dịch" value={evalData.transactionCount ?? 0} icon={appImages.icon_circle_dollar_sign} iconColor="#F57127" />
            <EvalMetric
              label="Số công"
              value={expectedWork != null ? `${totalWork}/${expectedWork}` : `${totalWork}`}
              icon={appImages.icon_cham_cong}
              iconColor="#1277EB"
            />
          </Flex>
        </Box>
      </Box>

      {/* ===== Tấm trắng bo góc trên ===== */}
      <Box mt="-20px" bg={APP.surface} borderTopRadius="24px" pt="16px" px="20px" pb="90px" position="relative">
        {searchOpen && (
          <Box mb="18px">
            <Flex h="50px" borderRadius="12px" border="1px solid" borderColor="#DCE1EA" align="center" px="12px" gap="8px">
              <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
              <Input
                variant="unstyled"
                autoFocus
                placeholder="Tìm chức năng..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                fontSize="14px"
                fontWeight={500}
              />
              <Text
                as="button"
                onClick={() => (searchValue ? setSearchValue("") : setSearchOpen(false))}
                fontSize="14px"
                fontWeight={500}
                color={APP.primary200}
              >
                {searchValue ? "Xóa" : "Đóng"}
              </Text>
            </Flex>

            <Box mt="12px" border="1px solid" borderColor={APP.cardBorder} borderRadius="14px" overflow="hidden">
              <Box pt="12px" px="12px">
                <Text fontSize="15px" fontWeight={600} color={APP.black}>
                  {searchValue ? "Kết quả tìm kiếm" : "Gợi ý chức năng"}
                </Text>
              </Box>
              {searchResults.length ? (
                searchResults.map((item, i) => (
                  <Flex
                    key={item.id}
                    as="button"
                    type="button"
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchValue("");
                      item.onPress();
                    }}
                    align="center"
                    gap="12px"
                    px="12px"
                    py="12px"
                    w="100%"
                    borderBottom={i !== searchResults.length - 1 ? "1px solid" : "none"}
                    borderColor={APP.cardBorder}
                  >
                    <Flex w="42px" h="42px" borderRadius="10px" border="1px solid" borderColor={APP.cardBorder} align="center" justify="center">
                      <AppIcon src={item.icon} size={22} color={APP.tabActive} />
                    </Flex>
                    <Box flex="1" textAlign="left">
                      <Text fontSize="14px" fontWeight={600} color={APP.black} whiteSpace="normal">
                        {item.label.replace(/\n/g, " ")}
                      </Text>
                      <Text fontSize="12px" color="#64748B" mt="2px">
                        {item.section}
                      </Text>
                    </Box>
                    <Text fontSize="12px" color={APP.primary200}>
                      Mở
                    </Text>
                  </Flex>
                ))
              ) : (
                <Flex px="12px" py="18px" justify="center">
                  <Text fontSize="13px" color="#64748B">
                    Không tìm thấy chức năng phù hợp
                  </Text>
                </Flex>
              )}
            </Box>
          </Box>
        )}

        {SECTIONS.map((section, si) => {
          const items = ACTIONS.filter((a) => a.section === section);
          if (!items.length) return null;
          return (
            <Box key={section} mt={si === 0 ? "0" : "18px"}>
              <Text fontSize="16px" fontWeight={600} color={APP.black}>
                {section}
              </Text>
              <TileGrid>
                {items.map((a) => (
                  <IconTile key={a.id} icon={a.icon} label={a.label} onClick={a.onPress} />
                ))}
              </TileGrid>
            </Box>
          );
        })}

        {/* Tin tức nội bộ */}
        <Box mt="18px">
          <Text fontSize="16px" fontWeight={600} color={APP.black}>
            Tin tức nội bộ
          </Text>
          <NewsSlider items={NEWS} />
        </Box>

        {/* Quản lý (chỉ hiện với quản lý / phòng ban hỗ trợ) */}
        {showManagement && (
          <Box mt="18px">
            <Text fontSize="16px" fontWeight={600} color={APP.black}>
              Quản lý
            </Text>
            <TileGrid>
              {ACTIONS.filter((a) => a.section === "Quản lý").map((a) => (
                <IconTile key={a.id} icon={a.icon} label={a.label} onClick={a.onPress} />
              ))}
            </TileGrid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
